// @proref/schemas — Zod validation schemas shared by web and api.
// Define each domain's schemas here and import on both ends so client and server never drift.
import { z } from 'zod';

// ─── Auth ────────────────────────────────────────────────────────────────────

// Signup carries only the invite token + chosen password — the user's name was set by
// the admin on the invite row and is copied into users.full_name server-side. The form
// no longer collects a name, so we don't accept one over the wire (defensive: even if a
// stale client sends `full_name`, Zod's default mode would strip unknown keys but we'd
// rather not have two sources of truth flowing through the signup pipeline).
export const signupSchema = z.object({
  invite_token: z.string().min(1),
  password: z.string().min(8).max(72),
});

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

export const refreshSchema = z.object({
  refresh_token: z.string().min(1),
});

export const forgotPasswordSchema = z.object({
  email: z.string().email(),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8).max(72),
});

export type SignupInput = z.infer<typeof signupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type RefreshInput = z.infer<typeof refreshSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

// ─── Federations ───────────────────────────────────────────────────────────────

// IANA timezone check that works in both the browser and Node — driving streak
// resets and daily selection off a bad zone would silently break those jobs.
function isValidTimeZone(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const createFederationSchema = z.object({
  name: z.string().min(1).max(120).trim(),
  // Short badge shown next to a member's role in the app chrome (e.g. "HRS"). Uppercased so it
  // renders consistently however the admin typed it; 2–12 chars covers real federation acronyms.
  acronym: z.string().trim().min(2).max(12).transform((s) => s.toUpperCase()),
  country: z.string().min(2).max(100).trim(),
  timezone: z.string().refine(isValidTimeZone, 'Must be a valid IANA timezone (e.g. Europe/Zagreb)'),
});

export type CreateFederationInput = z.infer<typeof createFederationSchema>;

// Partial update — any subset of a federation's editable fields (PATCH semantics). super_admin
// only. `.partial()` keeps every field's own validation (e.g. the timezone refine) but optional.
export const updateFederationSchema = createFederationSchema.partial();

export type UpdateFederationInput = z.infer<typeof updateFederationSchema>;

// Path param for the mutable federation routes (PATCH/DELETE /v1/federations/:id).
export const federationIdParamSchema = z.object({
  id: z.string().uuid(),
});

export type FederationIdParam = z.infer<typeof federationIdParamSchema>;

// ─── Invites ─────────────────────────────────────────────────────────────────

// An invite can only ever onboard a referee or a federation_admin — super_admin is
// seeded, never invited (the DB CHECK invites_role_not_super_admin enforces this too).
// federation_id / sport_id are optional here because they only apply when a super_admin
// invites into a specific federation; for a federation_admin the server derives both
// from the JWT and ignores any values sent by the client.
export const createInviteSchema = z.object({
  // The admin sets the recipient's name on the invite (not the user, at signup) so the
  // identifier is consistent across analytics + emails and the signup form is just
  // "set your password". Same length constraints as users.full_name.
  full_name: z.string().min(1).max(100).trim(),
  // Normalised at the edge so the "is this email already a user?" check in createInvite is an
  // exact match, and so two invites for the same person can't differ only by case/whitespace.
  // Supabase Auth lowercases emails anyway, so this keeps invites.email aligned with auth.users.
  email: z.string().trim().toLowerCase().pipe(z.string().email()),
  role: z.enum(['referee', 'federation_admin']),
  federation_id: z.string().uuid().optional(),
  sport_id: z.string().uuid().optional(),
});

export type CreateInviteInput = z.infer<typeof createInviteSchema>;

// One request can carry this many invites. The cap is about the request, not the database: each
// row is a DB write plus an SMTP send, run one at a time, so an unbounded paste would hold the
// connection open for minutes. A federation onboarding a whole roster does it in batches.
export const MAX_BULK_INVITES = 50;

// Bulk invite — the same per-row shape as a single invite, so every rule (tenancy resolution,
// EMAIL_TAKEN, INVITE_PENDING) is enforced by exactly the same code path rather than a parallel
// implementation that could drift. The client puts the batch's chosen federation/sport on every
// row; a federation_admin's is ignored and derived from the JWT, as it is for a single invite.
export const bulkInviteSchema = z.object({
  invites: z.array(createInviteSchema).min(1).max(MAX_BULK_INVITES),
});

export type BulkInviteInput = z.infer<typeof bulkInviteSchema>;

// Path param for invite-scoped routes (e.g. POST /v1/invites/:id/resend). The id is a
// real uuid, so a malformed value fails fast as a 400 rather than reaching the DB.
export const inviteIdParamSchema = z.object({
  id: z.string().uuid(),
});

export type InviteIdParam = z.infer<typeof inviteIdParamSchema>;

// Query for GET /v1/invites/check?token=... — the unauthenticated lookup the signup page
// hits on mount to greet the user by name + show which federation/sport they're joining,
// WITHOUT burning the token. Validated as a non-empty string (tokens are URL-safe random
// strings, not uuids — `.min(1)` is the right shape constraint here).
export const inviteCheckQuerySchema = z.object({
  token: z.string().min(1),
});

export type InviteCheckQuery = z.infer<typeof inviteCheckQuerySchema>;

// ─── Users ───────────────────────────────────────────────────────────────────

// Path param for DELETE /v1/users/:id (remove a user from the roster). A real uuid, so a
// malformed id fails fast as a 400 rather than reaching the DB.
export const userIdParamSchema = z.object({
  id: z.string().uuid(),
});

export type UserIdParam = z.infer<typeof userIdParamSchema>;

// Partial update of one roster member (PATCH /v1/users/:id) — the admin-side edit form.
// Deliberately NOT here:
//   • email — it's the Supabase Auth identity, so changing it means auth.admin.updateUserById
//     plus re-verification; a rename is not an identity change and shouldn't quietly become one.
//   • xp / current_streak — earned counters, never hand-edited, or the leaderboard is fiction.
//   • role: 'super_admin' — the platform operator is seeded, never promoted through the API
//     (users_tenancy_by_role would also reject the row, since a super_admin must be global).
export const updateUserSchema = z
  .object({
    full_name: z.string().min(1).max(100).trim().optional(),
    // Nullable so the form can clear a licence back to "none" with the same control that sets it.
    // An empty box means "no licence", so normalise '' to NULL here rather than storing a blank
    // string the UI would then have to treat as absent everywhere it renders.
    licence_level: z
      .string()
      .trim()
      .max(60)
      .nullable()
      .optional()
      .transform((v) => (v === '' ? null : v)),
    role: z.enum(['referee', 'federation_admin']).optional(),
    // Re-homing a member into another tenant. super_admin only — the service rejects a
    // federation_admin sending either, rather than silently ignoring it (a PATCH that reports
    // success while dropping the change is worse than a refusal).
    federation_id: z.string().uuid().optional(),
    sport_id: z.string().uuid().optional(),
  })
  // An empty PATCH would otherwise pass validation and write nothing while reporting success.
  .refine((body) => Object.keys(body).length > 0, { message: 'No fields to update' });

export type UpdateUserInput = z.infer<typeof updateUserSchema>;

// ─── Sports & rules ──────────────────────────────────────────────────────────

// Path param for the sport-scoped read routes (/v1/sports/:slug, /v1/sports/:slug/rules).
// Slugs are lower-case, hyphenated content keys (e.g. "handball") — never user input we
// persist, but validated so a malformed param fails fast as a 400 rather than a DB miss.
export const sportSlugParamSchema = z.object({
  slug: z.string().min(1).max(60),
});

export type SportSlugParam = z.infer<typeof sportSlugParamSchema>;

// Create-sport payload (super_admin only). The slug is the stable content key the rules
// endpoint nests under, so it's constrained to a lower-case hyphenated form here — the
// same shape validated on read — to keep URLs and the DB's unique(slug) clean.
export const createSportSchema = z.object({
  name: z.string().min(1).max(80).trim(),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lower-case letters, numbers and single hyphens only'),
  rulebook_name: z.string().min(1).max(120).trim(),
});

export type CreateSportInput = z.infer<typeof createSportSchema>;

// Partial update — any subset of a sport's fields (PATCH semantics). super_admin only. Editing
// the slug re-keys the sport's read URLs (/v1/sports/:slug/…) but not its data (clips/rules are
// keyed by sport_id); the unique(slug) constraint still guards against collisions (→ 409).
export const updateSportSchema = createSportSchema.partial();

export type UpdateSportInput = z.infer<typeof updateSportSchema>;

// Path param for the mutable sport routes (PATCH/DELETE /v1/sports/:id). By id, not slug — a slug
// can be edited, so the stable uuid is the right handle for update/delete (the read routes stay
// slug-addressed; Fastify keys routes by method so GET :slug and PATCH :id coexist).
export const sportIdParamSchema = z.object({
  id: z.string().uuid(),
});

export type SportIdParam = z.infer<typeof sportIdParamSchema>;

// ─── Rule articles (authoring — super_admin only) ──────────────────────────────

// Path param for the per-rule routes (PATCH/DELETE /v1/rules/:id).
export const ruleArticleIdParamSchema = z.object({
  id: z.string().uuid(),
});

export type RuleArticleIdParam = z.infer<typeof ruleArticleIdParamSchema>;

// One authored rule article. `article_number` is free text ("8" / "8:5") since rule-books
// don't number cleanly as integers; `key_points` is a short bullet list (kept flat — the
// referee-facing radar reads them as-is). This is also the shape the AI wizard returns per
// extracted article (see extractRulesSchema), so drafts drop straight into create/bulk-create.
export const createRuleArticleSchema = z.object({
  article_number: z.string().min(1).max(20).trim(),
  title: z.string().min(1).max(200).trim(),
  summary: z.string().min(1).max(2000).trim(),
  key_points: z.array(z.string().min(1).max(300)).max(30).default([]),
});

export type CreateRuleArticleInput = z.infer<typeof createRuleArticleSchema>;

// Partial update — any subset of the authored fields (PATCH semantics).
export const updateRuleArticleSchema = createRuleArticleSchema.partial();

export type UpdateRuleArticleInput = z.infer<typeof updateRuleArticleSchema>;

// Bulk create (the wizard's "Create N rules" confirm step). Capped so a runaway extraction
// can't insert an unbounded batch; the whole set inserts in order under one sport.
export const bulkCreateRuleArticlesSchema = z.object({
  articles: z.array(createRuleArticleSchema).min(1).max(100),
});

export type BulkCreateRuleArticlesInput = z.infer<typeof bulkCreateRuleArticlesSchema>;

// AI-extraction request: the rule-book PDF, base64-encoded client-side, forwarded straight to
// Claude. The length guard is a coarse pre-check (~25 MB of base64 ≈ ~18 MB of PDF) so an
// oversized upload is a fast 400 rather than a slow round-trip; Claude's own limit is 32 MB.
export const extractRulesSchema = z.object({
  pdf_base64: z.string().min(1).max(25_000_000),
});

export type ExtractRulesInput = z.infer<typeof extractRulesSchema>;

// ─── Clips ───────────────────────────────────────────────────────────────────

// Path param for the single-clip routes (/v1/clips/:id, …/publish). The id is the clip
// (and S3 path) UUID — validated so a malformed param is a fast 400, not a DB miss.
export const clipIdParamSchema = z.object({
  id: z.string().uuid(),
});

export type ClipIdParam = z.infer<typeof clipIdParamSchema>;

// Query params for GET /v1/clips. All optional; omitted = no filter on that axis (all visible
// clips). Validated so a malformed UUID is a fast 400 instead of an empty result that looks like
// "no clips here". `rule_article_id` powers the referee library's rule-nav bar; `sport_id` +
// `federation_id` power the super_admin clips console (pick a sport, then narrow to a federation).
// The filters compose with — never override — the actor's own visibility scope in the service.
export const listClipsQuerySchema = z.object({
  rule_article_id: z.string().uuid().optional(),
  // Managed-vocabulary tag filter — the referee library's tag chips narrow to clips carrying this
  // tag. Composes with the other filters (AND), same as rule_article_id.
  tag_id: z.string().uuid().optional(),
  sport_id: z.string().uuid().optional(),
  federation_id: z.string().uuid().optional(),
  // Restrict to OFFICIAL (platform) clips — federation_id IS NULL. super_admin uses this to build
  // an Official clip exam's question set. Coerced from the `?official=true` query string.
  official: z.coerce.boolean().optional(),
  // Restrict to PUBLISHED clips. Referees are always forced to published server-side; this lets an
  // ADMIN ask for the same view, so the referee-facing library shows the library referees actually
  // see rather than silently including the admin's own drafts. Coerced from `?published=true`.
  published: z.coerce.boolean().optional(),
  // Optional windowing for the referee library's infinite scroll. Both coerced from the query
  // string. When `limit` is omitted the endpoint returns the full scoped list (unchanged for the
  // admin console + Home count); when present it returns just that page (offset defaults to 0).
  limit: z.coerce.number().int().min(1).max(100).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export type ListClipsQuery = z.infer<typeof listClipsQuerySchema>;

// Hard ceiling on a source upload. Clips are short (5s–2min); 500 MB is generous for a
// high-bitrate MP4 and is pinned into the presigned PUT so S3 rejects anything larger.
//
// Deliberately NOT raised to accommodate the 5-minute duration cap below. The two interact: 500 MB
// over 5 minutes implies a ~13 Mbps source, which is good 1080p but not 4K. That trade is on
// purpose — §5's upload is a SINGLE presigned PUT with no resume, and that design only holds while
// files stay small enough that retrying a failed upload is cheap. Raising this forces resumable
// multipart (per-part signing through the API) before it buys anything the product needs.
export const MAX_CLIP_UPLOAD_BYTES = 500 * 1024 * 1024;

// Hard ceiling on clip DURATION, enforced in two places that can't be talked past:
//   1. the browser probes the file and refuses before the presign (good UX, spoofable),
//   2. MediaConvert's InputClipping truncates the job at this length (the actual guarantee).
//
// Frame rate is NOT capped anywhere — see video-probe.ts for why measuring it client-side was tried
// and dropped. Its multiplier is worth ~$1.50/month at the largest plan; duration is worth ~$28 per
// bad upload, which is the whole reason this constant exists.
//
// It exists because MediaConvert bills per minute of OUTPUT, and duration is only known AFTER the
// transcode runs — so a post-hoc duration check cannot prevent the cost it discovers. Size alone
// is not a proxy: 500 MB of heavily-compressed low-bitrate video is ~7 HOURS, which at the
// 3-rendition ladder is ~$28 of transcode for one upload. Clipping caps any single clip at ~$0.31.
export const MAX_CLIP_DURATION_SEC = 300;

// Body for POST /v1/media/upload-url — create the draft clip row + get a presigned PUT.
// v1 accepts MP4 only (content_type is pinned into the signature). super_admin is global,
// so it must say which federation/sport the clip lands in; a federation_admin's tenancy is
// derived from the JWT and these two fields are ignored for them (anti-cross-tenant).
export const uploadUrlSchema = z.object({
  title: z.string().min(1).max(200).trim(),
  description: z.string().max(2000).trim().optional(),
  content_type: z.literal('video/mp4'),
  size_bytes: z.number().int().positive().max(MAX_CLIP_UPLOAD_BYTES),
  federation_id: z.string().uuid().optional(),
  sport_id: z.string().uuid().optional(),
});

export type UploadUrlInput = z.infer<typeof uploadUrlSchema>;

// Body for PATCH /v1/clips/:id — the admin tags a clip at the decision point. Every field
// is optional (partial update), but at least one must be present, and a tagged clip needs
// these set before it can be published (enforced server-side, not here).
// A clip carries at most this many managed tags — small enough to stay a glanceable chip row on a
// card, and the cap the admin editor enforces. Shared so the API service and the web form agree.
export const MAX_CLIP_TAGS = 3;

// Max length of a decision string — the clip's correct call, each answer option, and the option a
// referee submits. Real officiating calls are full sentences ("2-minute suspension plus a free
// throw to the opponents, and the team official is warned"), and the old 120 was clipping them.
//
// These THREE must stay equal: an attempt's `selected_option` is validated against the clip's
// `options`, so a shorter cap on the attempt would make a long-but-valid option unanswerable.
export const MAX_DECISION_LEN = 300;

export const tagClipSchema = z
  .object({
    title: z.string().min(1).max(200).trim().optional(),
    description: z.string().max(2000).trim().optional(),
    rule_article_id: z.string().uuid().optional(),
    decision_timestamp_ms: z.number().int().nonnegative().optional(),
    correct_decision: z.string().min(1).max(MAX_DECISION_LEN).trim().optional(),
    options: z.array(z.string().min(1).max(MAX_DECISION_LEN).trim()).min(2).max(8).optional(),
    commentary_text: z.string().max(5000).trim().optional(),
    // The COMPLETE set of managed-vocabulary tag ids for this clip (set semantics: present ⇒ the
    // clip's tags become exactly this list, so [] clears them). Capped at MAX_CLIP_TAGS; the ids
    // must belong to the clip's federation + sport (enforced server-side). Omit to leave tags as-is.
    tag_ids: z.array(z.string().uuid()).max(MAX_CLIP_TAGS).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to update' });

export type TagClipInput = z.infer<typeof tagClipSchema>;

// PUT /v1/admin/daily-queue/order — the admin's reordered rotation. `clip_ids` is the COMPLETE,
// full-replace ordered list of a federation+sport's published clips (not a delta), so the endpoint
// can just write 1..N straight down the array — same "present ⇒ becomes exactly this" semantics as
// tagClipSchema's tag_ids. Capped generously above any realistic single federation's library.
const MAX_DAILY_QUEUE_CLIPS = 2000;

export const reorderDailyQueueSchema = z.object({
  // super_admin only — a federation_admin's own federation is used regardless of this field.
  federation_id: z.string().uuid().optional(),
  sport_id: z.string().uuid(),
  clip_ids: z.array(z.string().uuid()).min(1).max(MAX_DAILY_QUEUE_CLIPS),
});

export type ReorderDailyQueueInput = z.infer<typeof reorderDailyQueueSchema>;

// GET /v1/admin/daily-queue — the upcoming rotation preview. sport_id is required (mirrors the
// admin clips console's own sport-then-federation drill-down); federation_id is super_admin-only,
// same convention as reorderDailyQueueSchema. days is coerced from the query string.
export const dailyQueueQuerySchema = z.object({
  federation_id: z.string().uuid().optional(),
  sport_id: z.string().uuid(),
  days: z.coerce.number().int().min(1).max(60).optional(),
});

export type DailyQueueQuery = z.infer<typeof dailyQueueQuerySchema>;

// ─── Clip tags (managed vocabulary) ────────────────────────────────────────────

// Path param for the single-tag routes (DELETE /v1/clip-tags/:id).
export const clipTagIdParamSchema = z.object({ id: z.string().uuid() });
export type ClipTagIdParam = z.infer<typeof clipTagIdParamSchema>;

// Query for GET /v1/clip-tags. A referee / federation_admin gets its own federation + sport list
// (the ids are ignored, derived from the JWT); a super_admin — being global — must name the
// federation + sport whose vocabulary it wants (mirrors the clips console filters).
export const listClipTagsQuerySchema = z.object({
  federation_id: z.string().uuid().optional(),
  sport_id: z.string().uuid().optional(),
});
export type ListClipTagsQuery = z.infer<typeof listClipTagsQuerySchema>;

// Body for POST /v1/clip-tags — add a label to the vocabulary. A federation_admin's federation +
// sport are JWT-derived (the ids are ignored); a super_admin must name both. Label is a short chip;
// case-insensitive uniqueness per (federation, sport) is enforced by the DB (409 on collision).
//
// The fixed tag-colour palette. Stored as a key on `clip_tags.color` (a DB CHECK pins the same set —
// keep both in sync); the web layer maps each key to a swatch. 'slate' is the neutral default.
export const CLIP_TAG_COLORS = ['slate', 'red', 'amber', 'green', 'teal', 'blue', 'violet', 'pink'] as const;
export type ClipTagColor = (typeof CLIP_TAG_COLORS)[number];

// `color` is optional — the API defaults it to 'slate' so an older client that omits it still works.
export const createClipTagSchema = z.object({
  label: z.string().min(1).max(40).trim(),
  color: z.enum(CLIP_TAG_COLORS).optional(),
  federation_id: z.string().uuid().optional(),
  sport_id: z.string().uuid().optional(),
});
export type CreateClipTagInput = z.infer<typeof createClipTagSchema>;

// Body of the internal transcoding-complete webhook (EventBridge → API). Reports the clip
// whose MediaConvert job finished. The HLS/poster paths follow the <uuid>/… convention and
// are derived server-side; the event only needs to name the clip, the outcome, and (on
// success) the measured duration. Authenticated by a static secret header, not a JWT.
export const transcodingCompleteSchema = z.object({
  clip_id: z.string().uuid(),
  status: z.enum(['ready', 'failed']).default('ready'),
  duration_sec: z.number().positive().optional(),
});

export type TranscodingCompleteInput = z.infer<typeof transcodingCompleteSchema>;

// ─── Attempts (M6 — the core loop) ───────────────────────────────────────────

// Body for POST /v1/attempts — a referee answers a clip. user_id is NEVER accepted from
// the client (derived from the JWT); is_first is decided server-side. selected_option is
// validated against the clip's actual options in the service (it can't be known here), so
// a value outside the option set is a clean 400 there, not a recorded bad attempt.
export const createAttemptSchema = z.object({
  clip_id: z.string().uuid(),
  // Same cap as the clip's `options` (MAX_DECISION_LEN) — they must match or a long option
  // couldn't be submitted as an answer.
  selected_option: z.string().min(1).max(MAX_DECISION_LEN).trim(),
});

export type CreateAttemptInput = z.infer<typeof createAttemptSchema>;

// ─── Discussion (M7) ─────────────────────────────────────────────────────────

// Path param for the comment-scoped routes (PATCH/DELETE /v1/discussions/:id, …/vote).
export const discussionIdParamSchema = z.object({
  id: z.string().uuid(),
});

export type DiscussionIdParam = z.infer<typeof discussionIdParamSchema>;

// Body for POST /v1/clips/:id/discussions — a new comment or a reply. clip_id comes from the
// path and user_id from the JWT (never the body). parent_id, when present, must be a comment
// on the SAME clip — the service validates that so a reply can't be grafted across clips.
export const createDiscussionSchema = z.object({
  body: z.string().min(1).max(4000).trim(),
  parent_id: z.string().uuid().optional(),
});

export type CreateDiscussionInput = z.infer<typeof createDiscussionSchema>;

// Body for PATCH /v1/discussions/:id — edit a comment's text (author only, enforced in the
// service). Only the body is mutable; stamping edited_at is the server's job.
export const updateDiscussionSchema = z.object({
  body: z.string().min(1).max(4000).trim(),
});

export type UpdateDiscussionInput = z.infer<typeof updateDiscussionSchema>;

// ─── Exams (authoring — federation_admin + super_admin) ─────────────────────────

export const examTypeSchema = z.enum(['rule_test', 'video_clip_exam']);

// Create-exam payload. `type` is fixed at creation (questions depend on it). Tenancy is
// role-branched in the service: a federation_admin authors a FEDERATION add-on test inside its
// own federation + sport (JWT-derived — no body tenancy needed); a super_admin authors a PLATFORM
// (generic) test, which is federation-less, so it only names the `sport_id`. Platform tests must
// be rule tests (clip exams need a federation's clips). time_limit_sec null/omitted = untimed.
export const createExamSchema = z.object({
  title: z.string().min(1).max(120).trim(),
  type: examTypeSchema,
  time_limit_sec: z
    .number()
    .int()
    .positive()
    .max(4 * 60 * 60)
    .nullable()
    .optional(),
  // super_admin platform tests name the sport here; ignored for a federation_admin (JWT-derived).
  sport_id: z.string().uuid().optional(),
  // Mark this an "Official" (proctored) rule test — auto-timed 90s × question count. Rule-test only
  // (the service rejects it on a clip exam). Usually left off at create and toggled in the editor.
  is_proctored: z.boolean().optional(),
});

export type CreateExamInput = z.infer<typeof createExamSchema>;

// Editable after creation. title + time limit always; `type` and `sport_id` only while the exam
// has NO questions yet (the editor now hosts these — the create modal is gone) and are guarded in
// the service (0-questions, tenancy). `is_proctored` toggles the Official/proctored flag; when set,
// the service derives time_limit_sec (90s × question count) and ignores any time_limit_sec here.
export const updateExamSchema = z.object({
  title: z.string().min(1).max(120).trim().optional(),
  time_limit_sec: z
    .number()
    .int()
    .positive()
    .max(4 * 60 * 60)
    .nullable()
    .optional(),
  type: examTypeSchema.optional(),
  sport_id: z.string().uuid().optional(),
  is_proctored: z.boolean().optional(),
  // Set/replace the Official-exam access password (min 4 chars). `null` clears it. Never returned.
  password: z.string().min(4).max(100).nullable().optional(),
});

export type UpdateExamInput = z.infer<typeof updateExamSchema>;

// A referee starting an attempt supplies the access password for an Official (proctored) exam.
// Omitted/ignored for a normal exam.
export const startExamAttemptSchema = z.object({
  password: z.string().min(1).max(100).optional(),
});

export type StartExamAttemptInput = z.infer<typeof startExamAttemptSchema>;

export const examIdParamSchema = z.object({ id: z.string().uuid() });
export type ExamIdParam = z.infer<typeof examIdParamSchema>;

// Params for the nested admin result route (/v1/exams/:id/results/:attemptId). Both ids are
// validated because the service checks the attempt actually belongs to that exam — otherwise an
// admin could delete a result from an exam they don't administer by passing any attempt id.
export const examResultParamSchema = z.object({
  id: z.string().uuid(),
  attemptId: z.string().uuid(),
});
export type ExamResultParam = z.infer<typeof examResultParamSchema>;

export const examQuestionIdParamSchema = z.object({ id: z.string().uuid() });
export type ExamQuestionIdParam = z.infer<typeof examQuestionIdParamSchema>;

// super_admin narrows the cross-tenant list to a federation / sport; ignored for a
// federation_admin (already pinned) and a referee (own federation + sport).
export const listExamsQuerySchema = z.object({
  federation_id: z.string().uuid().optional(),
  sport_id: z.string().uuid().optional(),
});

export type ListExamsQuery = z.infer<typeof listExamsQuerySchema>;

// ─── Question bank / library (M13 — reusable rule-test questions) ────────────────
// Filters for GET /v1/question-bank (admin-only). sport_id narrows a super_admin to one sport
// (a federation_admin is pinned to its own); rule_article_id filters to one rule; `search` is a
// case-insensitive substring match on the question text. All optional — omitted = the whole
// visible library.
export const listQuestionBankQuerySchema = z.object({
  sport_id: z.string().uuid().optional(),
  rule_article_id: z.string().uuid().optional(),
  search: z.string().trim().max(200).optional(),
});

export type ListQuestionBankQuery = z.infer<typeof listQuestionBankQuerySchema>;

// A question's shape depends on the exam's kind, which the schema can't see — so all target
// fields are optional here and the service validates against the exam type (rule_test needs
// rule_article_id + options + correct_answers; video_clip_exam needs clip_id only). position
// is optional (appended to the end when omitted).
//
// correct_answers is an ARRAY: a "select all that apply" question can mark several options
// correct, which is what partial credit grades against. One element is the ordinary
// single-answer question. Capped at the option ceiling — marking every option correct is
// degenerate but harmless, so it isn't worth a separate rule.
export const createExamQuestionSchema = z.object({
  rule_article_id: z.string().uuid().optional(),
  clip_id: z.string().uuid().optional(),
  question_text: z.string().min(1).max(500).trim().optional(),
  options: z.array(z.string().min(1).max(200)).min(2).max(8).optional(),
  correct_answers: z.array(z.string().min(1).max(200)).min(1).max(8).optional(),
  position: z.number().int().positive().optional(),
});

export type CreateExamQuestionInput = z.infer<typeof createExamQuestionSchema>;

export const updateExamQuestionSchema = createExamQuestionSchema;
export type UpdateExamQuestionInput = z.infer<typeof updateExamQuestionSchema>;

// Body for POST /v1/exams/:id/questions/batch — add several clip questions to a video_clip_exam
// in one call (the "build a test out of selected videos" authoring flow). Only clip ids: each
// clip question reuses the clip's own decision + options, so nothing else is authored. Capped at
// 50 so one request can't append an unbounded number of questions.
export const addExamClipQuestionsSchema = z.object({
  clip_ids: z.array(z.string().uuid()).min(1).max(50),
});

export type AddExamClipQuestionsInput = z.infer<typeof addExamClipQuestionsSchema>;

// Body for POST /v1/exams/:id/questions/rule-batch — add several rule-test questions in one call
// (the "pick from the library / stage locally, save once" flow). Each carries its own rule article,
// options and answer (same per-question rules as createExamQuestionSchema, but here they're
// required — a staged question is always complete). Capped at 50 per request.
export const addExamRuleQuestionsSchema = z.object({
  questions: z
    .array(
      z.object({
        rule_article_id: z.string().uuid(),
        question_text: z.string().min(1).max(500).trim().optional(),
        options: z.array(z.string().min(1).max(200)).min(2).max(8),
        correct_answers: z.array(z.string().min(1).max(200)).min(1).max(8),
      }),
    )
    .min(1)
    .max(50),
});

export type AddExamRuleQuestionsInput = z.infer<typeof addExamRuleQuestionsSchema>;

// ─── Exam taking (referee + federation_admin) ───────────────────────────────────

export const examAttemptIdParamSchema = z.object({ id: z.string().uuid() });
export type ExamAttemptIdParam = z.infer<typeof examAttemptIdParamSchema>;

// Autosave one answer for the attempt. selected_answers holds every option the taker has
// ticked — several on a "select all that apply" question, one on an ordinary one. An EMPTY
// array is valid and meaningful: it's how the taker un-ticks their last choice, leaving the
// question saved-but-unanswered rather than stuck on a stale pick.
// time_spent_ms is optional client telemetry for exam analytics.
export const saveExamAnswerSchema = z.object({
  exam_question_id: z.string().uuid(),
  selected_answers: z.array(z.string().min(1).max(200)).max(8),
  time_spent_ms: z.number().int().nonnegative().optional(),
});
export type SaveExamAnswerInput = z.infer<typeof saveExamAnswerSchema>;

// Starting a generated practice test. The referee picks how many questions and whether to be
// timed; the questions themselves are drawn server-side from the question bank.
//
// The count is an ENUM, not a free integer: these are the sizes the federation's own exam
// regulations use, and an open number invites a 1-question "test" that games the history stats.
export const PRACTICE_TEST_SIZES = [10, 15, 20, 30] as const;

// Seconds per question when the practice test is timed. Same budget as an Official exam, so
// practising under the clock actually rehearses the real thing.
export const PRACTICE_SECONDS_PER_QUESTION = 90;

export const startPracticeTestSchema = z.object({
  question_count: z.union([z.literal(10), z.literal(15), z.literal(20), z.literal(30)]),
  timed: z.boolean(),
});
export type StartPracticeTestInput = z.infer<typeof startPracticeTestSchema>;

// The pass mark for an exam, as a fraction of the available points. A referee exam is a
// licensing instrument rather than a quiz, so the bar is deliberately high. Shared between the
// API (which stamps `passed` on an admin's results export) and the web app (which renders the
// verdict), so the two can never disagree about who passed.
export const EXAM_PASS_SCORE = 0.85;

// Proctoring tallies reported from an in-progress Official exam. The client sends RUNNING
// TOTALS, not increments: a fire-and-forget report can be lost or arrive out of order, and a
// total is idempotent under both. The server takes the greater of stored and reported, so a
// stale report can never lower a count.
//
// The ceiling is a sanity bound, not a policy — anything past it is a broken or hostile client,
// and clamping keeps one from writing an absurd number into an admin's report.
export const reportProctoringSchema = z.object({
  focus_lost_count: z.number().int().nonnegative().max(10_000),
  screenshot_attempts: z.number().int().nonnegative().max(10_000),
  fullscreen_exit_count: z.number().int().nonnegative().max(10_000),
});
export type ReportProctoringInput = z.infer<typeof reportProctoringSchema>;

// ─── Documents (M11) ─────────────────────────────────────────────────────────

export const documentTypeSchema = z.enum(['rulebook', 'commentary', 'signal_code', 'template']);
export const documentFormatSchema = z.enum(['pdf', 'docx']);

// Hard ceiling on a document upload. Rule-book PDFs are the biggest realistic case; 50 MB is
// generous and is pinned into the presigned PUT so S3 rejects anything larger.
export const MAX_DOCUMENT_UPLOAD_BYTES = 50 * 1024 * 1024;

// Body for POST /v1/documents/upload-url — create the row + get a presigned PUT. Tenancy is
// role-branched in the service (mirrors createExam): a super_admin omits federation_id → a PLATFORM
// document scoped to `sport_id`; a federation_admin's federation + sport are JWT-derived, and it
// must NOT name a foreign federation. `content_length` is pinned into the signature (exact size).
export const createDocumentSchema = z.object({
  title: z.string().min(1).max(200).trim(),
  type: documentTypeSchema,
  file_format: documentFormatSchema,
  content_length: z.number().int().positive().max(MAX_DOCUMENT_UPLOAD_BYTES),
  // super_admin platform docs name the sport here; ignored for a federation_admin (JWT-derived).
  sport_id: z.string().uuid().optional(),
  // a federation_admin may pass its own federation explicitly; a super_admin omits it (platform).
  federation_id: z.string().uuid().nullable().optional(),
});
export type CreateDocumentInput = z.infer<typeof createDocumentSchema>;

export const documentIdParamSchema = z.object({ id: z.string().uuid() });
export type DocumentIdParam = z.infer<typeof documentIdParamSchema>;

// ─── Bookmark notes (Bookmarked redesign) ────────────────────────────────────

export const bookmarkIdParamSchema = z.object({ id: z.string().uuid() });
export type BookmarkIdParam = z.infer<typeof bookmarkIdParamSchema>;

// Body for PATCH /v1/bookmarks/:id — set or clear a bookmark's personal note. An empty string
// clears it (the service maps '' → NULL); capped so a note stays a note, not an essay.
export const updateBookmarkNoteSchema = z.object({
  note: z.string().max(500),
});
export type UpdateBookmarkNoteInput = z.infer<typeof updateBookmarkNoteSchema>;

// GET /v1/documents?sport_id= — super_admin picks a sport; ignored for referee/federation_admin
// (their sport is JWT-derived).
export const listDocumentsQuerySchema = z.object({
  sport_id: z.string().uuid().optional(),
});
export type ListDocumentsQuery = z.infer<typeof listDocumentsQuerySchema>;

// ─── Webinars (M11) ──────────────────────────────────────────────────────────

export const webinarTypeSchema = z.enum(['live', 'group']);

// Where the webinar actually happens. Kept as a small closed set rather than a free-text field so
// the UI can label + icon it; 'other' is the escape hatch (Webex, Jitsi, a federation's own tool),
// whose display name is derived from the join link's hostname instead of a second column.
export const webinarPlatformSchema = z.enum(['teams', 'zoom', 'meet', 'other']);

// One person presenting. Deliberately free text, NOT a users FK: a speaker is usually an external
// expert (an EHF instructor, a guest from another federation) with no ProRef account at all.
export const webinarSpeakerSchema = z.object({
  name: z.string().min(1).max(120).trim(),
  // e.g. "EHF Delegate, Rules Committee" — optional; plenty of webinars just name the person.
  title: z.string().max(160).trim().optional(),
});

// A join link is only ever an https meeting URL. http:// is rejected outright rather than silently
// upgraded — a plain-http meeting link is far more likely a typo than a real room.
const joinUrlSchema = z.string().url().startsWith('https://').max(2000);

// More than a handful of speakers stops being a webinar and starts being a conference — and the
// card/modal layouts have nowhere to put them.
const MAX_WEBINAR_SPEAKERS = 6;

// Body for POST /v1/webinars. The host's scope is ALWAYS JWT-derived, never accepted from the client:
// a federation_admin schedules a webinar for its own federation (federation_id + sport_id from the
// JWT), a super_admin schedules a GENERAL one (federation_id + sport_id null). So the body only
// carries the human-authored fields. description/duration_min/capacity are optional; capacity null =
// unlimited seats. scheduled_at is an ISO timestamp.
export const createWebinarSchema = z.object({
  title: z.string().min(1).max(200).trim(),
  description: z.string().max(2000).trim().optional(),
  type: webinarTypeSchema,
  scheduled_at: z.string().datetime(),
  duration_min: z.number().int().positive().max(600).optional(),
  capacity: z.number().int().positive().max(100_000).optional(),
  // Both optional and independent: a host routinely schedules the session first and pastes the
  // meeting link days later (hence PATCH below), and may know the platform before the room exists.
  platform: webinarPlatformSchema.optional(),
  join_url: joinUrlSchema.optional(),
  speakers: z.array(webinarSpeakerSchema).max(MAX_WEBINAR_SPEAKERS).optional(),
});
export type CreateWebinarInput = z.infer<typeof createWebinarSchema>;

// Body for PATCH /v1/webinars/:id. Every field optional (patch semantics); the ones that are
// nullable in the DB accept an explicit null to CLEAR them — `join_url: null` unsets a link that
// was pasted by mistake, which `undefined` (omitted) could never express. Scope (federation/sport)
// and host are never editable: they're JWT-derived at creation and re-deriving them on edit would
// let an admin move a webinar between tenants.
export const updateWebinarSchema = z
  .object({
    title: z.string().min(1).max(200).trim().optional(),
    description: z.string().max(2000).trim().nullable().optional(),
    type: webinarTypeSchema.optional(),
    scheduled_at: z.string().datetime().optional(),
    duration_min: z.number().int().positive().max(600).nullable().optional(),
    capacity: z.number().int().positive().max(100_000).nullable().optional(),
    platform: webinarPlatformSchema.nullable().optional(),
    join_url: joinUrlSchema.nullable().optional(),
    speakers: z.array(webinarSpeakerSchema).max(MAX_WEBINAR_SPEAKERS).optional(),
  })
  // An empty patch is a client bug, not a no-op worth a 200 — fail loudly instead of writing
  // nothing and reporting success.
  .refine((body) => Object.keys(body).length > 0, { message: 'No fields to update' });
export type UpdateWebinarInput = z.infer<typeof updateWebinarSchema>;

export const webinarIdParamSchema = z.object({ id: z.string().uuid() });
export type WebinarIdParam = z.infer<typeof webinarIdParamSchema>;

// Query for GET /v1/webinars. 'upcoming' (default) is what every existing caller expects — soonest
// first, scheduled_at in the future. 'past' backs the new history tab: most-recent-first, so a
// referee/admin scrolling down sees what just happened before older sessions.
export const listWebinarsQuerySchema = z.object({
  when: z.enum(['upcoming', 'past']).default('upcoming'),
});
export type ListWebinarsQuery = z.infer<typeof listWebinarsQuerySchema>;

// ─── Leaderboard (M11) ───────────────────────────────────────────────────────

// GET /v1/leaderboard?scope=&period= — scope: federation (the caller's own, default) or global
// (all federations). period: 'all' (default, the running-total board) or a calendar window — see
// leaderboard.service.ts for how each window resolves to concrete instants.
export const leaderboardQuerySchema = z.object({
  scope: z.enum(['federation', 'global']).default('federation'),
  period: z.enum(['month', 'season', 'all']).default('all'),
});
export type LeaderboardQuery = z.infer<typeof leaderboardQuerySchema>;

// ─── Seasons (Leaderboard Period — super_admin managed calendar) ────────────

// Platform-wide, not per-federation — see leaderboard_window's migration comment for why a scoped
// season would make the global board incoherent. `starts_on`/`ends_on` are inclusive calendar
// dates (YYYY-MM-DD); the exclusion constraint in the DB is the real overlap guard, this just keeps
// obviously-malformed input (end before start) out before it reaches Postgres.
const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
export const createSeasonSchema = z
  .object({
    name: z.string().min(1).max(60).trim(),
    starts_on: dateStr,
    ends_on: dateStr,
  })
  .refine((v) => v.ends_on >= v.starts_on, {
    message: 'ends_on must be on or after starts_on',
    path: ['ends_on'],
  });
export type CreateSeasonInput = z.infer<typeof createSeasonSchema>;

// Partial update. Re-validating the start/end ordering needs both values present, so a caller that
// only patches one of them keeps whichever half it didn't send — done in the service, not here,
// since the schema alone can't see the existing row.
export const updateSeasonSchema = z.object({
  name: z.string().min(1).max(60).trim().optional(),
  starts_on: dateStr.optional(),
  ends_on: dateStr.optional(),
});
export type UpdateSeasonInput = z.infer<typeof updateSeasonSchema>;

export const seasonIdParamSchema = z.object({
  id: z.string().uuid(),
});
export type SeasonIdParam = z.infer<typeof seasonIdParamSchema>;

// ─── Notifications (M14) ─────────────────────────────────────────────────────

export const NOTIFICATION_PAGE_SIZE = 20;
export const MAX_NOTIFICATION_PAGE_SIZE = 50;
// One mark-read call covers a page of the list plus slack. Beyond this the client should send
// `all: true` rather than enumerating — a 5,000-id array is a request body, not a bulk operation.
export const MAX_NOTIFICATION_READ_IDS = 200;

// Cursor format: "<created_at ISO>|<uuid>". COMPOSITE, not a bare timestamp, because a fan-out
// INSERT stamps every row it writes with the same transaction `now()` — so two notifications
// delivered in one request share a created_at exactly, and a timestamp-only keyset would drop
// whichever row sorted second. Validated here so a malformed cursor is a 400 rather than a silently
// empty page.
const NOTIFICATION_CURSOR_RE =
  /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})\|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const listNotificationsQuerySchema = z.object({
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_NOTIFICATION_PAGE_SIZE)
    .default(NOTIFICATION_PAGE_SIZE),
  cursor: z.string().regex(NOTIFICATION_CURSOR_RE, 'Malformed cursor').optional(),
  // Deliberately NOT z.coerce.boolean(): `Boolean('false') === true`, so a coerced flag can only
  // ever be turned on. An explicit two-value enum means `?unread_only=false` actually means false.
  unread_only: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;

// Mark read: either an explicit set of ids (what the dropdown sends for what it just showed) or
// the whole inbox. Modelled as one object with a refine rather than a z.union so the 400 names the
// actual problem instead of dumping both branches' errors.
export const markNotificationsReadSchema = z
  .object({
    ids: z.array(z.string().uuid()).min(1).max(MAX_NOTIFICATION_READ_IDS).optional(),
    all: z.literal(true).optional(),
  })
  .refine((body) => (body.ids === undefined) !== (body.all === undefined), {
    message: 'Send either `ids` or `all: true`, not both',
  });
export type MarkNotificationsReadInput = z.infer<typeof markNotificationsReadSchema>;

// Per-channel switches for one category. Both optional so a client can flip one channel without
// having to restate the other — the API merges the patch over the stored value.
export const notificationChannelPrefsSchema = z.object({
  in_app: z.boolean().optional(),
  email: z.boolean().optional(),
});

// A partial preference patch. Categories are spelled out rather than z.record(z.enum(...)) because
// z.record infers a TOTAL Record, which would type a patch as if every category were always
// present. `account` is absent by design — it has no toggle (see NotificationCategory).
export const updateNotificationPrefsSchema = z
  .object({
    daily: notificationChannelPrefsSchema.optional(),
    exams: notificationChannelPrefsSchema.optional(),
    webinars: notificationChannelPrefsSchema.optional(),
    discussion: notificationChannelPrefsSchema.optional(),
    admin: notificationChannelPrefsSchema.optional(),
    platform: notificationChannelPrefsSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: 'No preferences to update' });
export type UpdateNotificationPrefsInput = z.infer<typeof updateNotificationPrefsSchema>;

// ─── Self-service account (M14 · /settings) ──────────────────────────────────

// The profile edit a user may perform on THEMSELVES. Deliberately just the display name: email is
// the login identity (changing it is a re-verification flow, not a text field), and role /
// federation / sport / licence_level are credentials someone else assigns — a referee who could
// set their own licence level could claim any grade they liked.
export const updateMeSchema = z.object({
  full_name: z.string().min(1).max(100).trim(),
});
export type UpdateMeInput = z.infer<typeof updateMeSchema>;

// Change your own password. `current_password` is what separates this from the emailed reset
// flow: it proves the person at the keyboard is the account holder and not someone who walked up
// to an unlocked laptop, so it is verified against Supabase before anything is written.
//
// Bounds match resetPasswordSchema (bcrypt truncates past 72 bytes, so a longer one is a false
// sense of security rather than a stronger password).
export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1),
    new_password: z.string().min(8).max(72),
  })
  .refine((body) => body.current_password !== body.new_password, {
    // Caught here rather than server-side so the message lands on the field. Submitting the same
    // password back would otherwise "succeed" and teach the user nothing.
    message: 'Choose a password different from your current one',
    path: ['new_password'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
